import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import Cookies from 'js-cookie';
import { isSubscriptionRequired, subscriptionRequiredRefusal } from './subscription-error-handler';
import { BILLING_STATUS_STALE_EVENT, READ_ONLY_REFUSED_EVENT } from './billing-events';
import { clearStash, readStash, returnPath } from './impersonation-stash';
import { isPlatformToken, jwtClaims } from './jwt';
import { isSessionEnded, SESSION_ENDED_KEY, sessionEndedReason } from './session-ended';
import { cookieTokenStore, secureCookie, tokenStore } from './token-store';
import { ACCESS_ENDED_MESSAGE, MOBILE_APP_DISABLED_MESSAGE, REFRESH_REUSED_MESSAGE, SESSION_ENDED_MESSAGE, SESSION_EXPIRED_EVENT, type SessionExpired } from './session-events';
import { isNative } from '@/platform/native';

export const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

declare module 'axios' {
  interface AxiosRequestConfig {
    /** Set by the app's outbox transport: the request goes out only with this user's token (the post-refresh resend included). */
    outboxIdentity?: { userId: number; orgId: number };
  }
}

/** The message of the error that stops an outbox request carrying another user's token (the transport turns it into WrongUserError). */
export const OUTBOX_SESSION_MISMATCH = "The signed-in session is not the outbox item's user";

const NO_REFRESH_TOKEN = 'No refresh token available';
/** The session was signed out or replaced while a refresh was in flight: its answer is dropped (the outbox retries). */
const SESSION_CHANGED = 'Session changed during refresh';

/** Errors reach callers that log them (and iOS copies console output to the device log): never with the refresh token. */
function withoutRefreshToken(error: unknown): unknown {
  if (axios.isAxiosError(error)) {
    const headers = error.config?.headers as { delete?: (name: string) => unknown } & Record<string, unknown> | undefined;
    if (typeof headers?.delete === 'function') headers.delete('X-Refresh-Token');
    if (headers) delete headers['X-Refresh-Token'];
  }
  return error;
}

/** A refresh answer lost on the network must not hang the app: it is retried once with the same token (key decision 6). */
const NATIVE_REFRESH_TIMEOUT_MS = 15_000;

/** The machine code of an error body: `errors.code` (Rust), or the older shapes. */
export function errorCode(data: unknown): string | undefined {
  const obj = (v: unknown): Record<string, unknown> => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {});
  const body = obj(data);
  const errors = obj(body.errors);
  const code = errors.code ?? body.code ?? obj(body.error).code ?? errors.type;
  return typeof code === 'string' ? code : undefined;
}

/** An outbox request, its resend after a refresh included, never goes out with another user's token. */
function assertOutboxSession(config: InternalAxiosRequestConfig): void {
  const expected = config.outboxIdentity;
  if (!expected) return;
  const header = config.headers?.get('Authorization');
  if (!header) return; // no token yet (a cold start): the server answers 401, and the resend after the refresh is checked here
  // a token that cannot be read, or lacks either claim, is refused too
  const claims = jwtClaims(typeof header === 'string' ? header.replace(/^Bearer /, '') : null);
  if (!claims || Number(claims.user_id) !== expected.userId || Number(claims.organization_id) !== expected.orgId) {
    throw new AxiosError(OUTBOX_SESSION_MISMATCH, 'ERR_OUTBOX_SESSION', config);
  }
}

class ApiClient {
  private client: AxiosInstance;
  private refreshPromise: Promise<string> | null = null;
  private accessToken: string | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      // the app's CORS is opened for its two WebView origins without credentials (key decision 4); it uses no cookies
      withCredentials: !isNative(),
    });

    // The tokens are read lazily from the store (the app swaps in its own store after this module loads).
    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor
    this.client.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const token = this.getAccessToken();
        if (token && config.headers && !config.headers.has('Authorization')) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        assertOutboxSession(config);
        return config;
      },
      (error: AxiosError) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => {
        return response;
      },
      async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

        // Don't try to refresh token for login/register/public endpoints
        const isAuthEndpoint = originalRequest.url?.includes('/auth/login') ||
                               originalRequest.url?.includes('/auth/register') ||
                               originalRequest.url?.includes('/auth/refresh') ||
                               // the app's sign-in (its 401 and 403 are the login screen's to show)
                               (originalRequest.url === '/v1/mobile/sessions' && originalRequest.method === 'post') ||
                               // the platform sign-in's own steps (a wrong code is a 401 to show, never a reason to refresh or leave)
                               originalRequest.url?.includes('/v1/platform/auth/') ||
                               // a refused Google code is the callback page's to report, not a reason to refresh or leave
                               originalRequest.url?.includes('/auth/google/exchange');

        // Don't redirect on 401 for endpoints that might be called from public pages
        const isPublicEndpoint = originalRequest.url?.includes('/organizations/') || // Public org lookup
                                 originalRequest.url === '/v1/organization'; // Might be called without auth

        // A read-only organization: no redirect, the page and its data stay. The banner learns of it at once.
        if (isSubscriptionRequired(error)) {
          // only a write can be refused for billing; skipping reads makes a refetch loop impossible by construction
          if (!['get', 'head', 'options'].includes((originalRequest.method ?? 'get').toLowerCase())) {
            window.dispatchEvent(new Event(BILLING_STATUS_STALE_EVENT));
            window.dispatchEvent(new CustomEvent(READ_ONLY_REFUSED_EVENT, { detail: subscriptionRequiredRefusal(error) }));
          }
          return Promise.reject(error);
        }

        if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
          // If this is a public endpoint, don't try to refresh or redirect
          if (isPublicEndpoint) {
            return Promise.reject(error);
          }

          // Ending an impersonation is the auth context's to handle (it needs the 401 itself).
          if (originalRequest.url?.includes('/v1/platform/impersonations/')) {
            return Promise.reject(error);
          }

          // A console call refused with 401: the platform session is gone. Back to the platform sign-in, never the customer one.
          if (originalRequest.url?.includes('/v1/platform/') && !originalRequest.url.includes('/v1/platform/auth/') && !this.isImpersonating) {
            this.clearTokens();
            window.location.href = '/platform/login';
            return Promise.reject(error);
          }

          // A customer call still in flight while the admin stops impersonating: the stop closes the session on purpose, so its
          // 401 is expected. The stop lands on the account page itself; ending it again here would send the admin elsewhere.
          if (this.stoppingImpersonation) {
            return Promise.reject(error);
          }

          // During impersonation there is no refresh token (intentionally). A 401
          // means the short-lived impersonation token expired or was rejected:
          // never mint a wrong-identity token — drop back to the platform console.
          if (this.isImpersonating && !this.getRefreshToken()) {
            // Single-flight: concurrent 401s (and the banner) share one stop-and-exit, so the exit never runs twice.
            this.endingImpersonation ??= this.endImpersonationAfterExpiry().finally(() => {
              this.endingImpersonation = null;
            });
            await this.endingImpersonation;
            return Promise.reject(error);
          }

          // A late 401 after the impersonation ended: the restored platform token is active. Never refresh with it or clear it; back to the console.
          if (isPlatformToken(tokenStore.getAccess())) {
            // Already on a console page (the stop navigated there): stay, a hard redirect would drop the admin on the console home.
            if (!window.location.pathname?.startsWith('/platform')) window.location.href = returnPath('/platform');
            return Promise.reject(error);
          }

          // The session ended on the server (signed in on another device, password changed...): no refresh can help. Say why on the sign-in screen.
          if (isSessionEnded(error)) {
            this.endSession(sessionEndedReason(error));
            return Promise.reject(error);
          }

          originalRequest._retry = true;

          // Another tab already refreshed: the shared cookie holds a newer access token than the one this request used. Retry with it instead of
          // spending the (rotating) refresh token a second time.
          const used = (originalRequest.headers?.get?.('Authorization') as string | undefined)?.replace(/^Bearer /, '');
          const stored = tokenStore.getAccess();
          if (stored && used && stored !== used) {
            this.accessToken = stored;
            originalRequest.headers.Authorization = `Bearer ${stored}`;
            return this.client(originalRequest);
          }

          try {
            const newToken = await this.refreshAccessToken();
            if (newToken && originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
            }
            return this.client(originalRequest);
          } catch (refreshError) {
            // A sign-out or another sign-in happened meanwhile: the current session is not this refresh's to end
            if (refreshError instanceof Error && refreshError.message === SESSION_CHANGED) {
              return Promise.reject(refreshError);
            }
            // The app never reloads the WebView (the outbox keeps running), and keeps the session unless the server refused it.
            if (isNative()) {
              this.endNativeSessionIfRefused(refreshError);
              return Promise.reject(refreshError);
            }
            this.endSession(isSessionEnded(refreshError) ? sessionEndedReason(refreshError) : null);
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  private getAccessToken(): string | null {
    // Try memory first, then cookies
    if (this.accessToken) {
      return this.accessToken;
    }
    const token = tokenStore.getAccess();
    if (token) {
      this.accessToken = token; // Cache in memory
    }
    return token;
  }

  /**
   * Always the cookie, never a copy kept in memory: every refresh rotates the token and the cookie is shared by every tab, so a copy would
   * replay an old token (after the 60 s grace the server ends the whole session for it).
   */
  private getRefreshToken(): string | null {
    return tokenStore.getRefresh();
  }

  private setTokens(accessToken: string, refreshToken: string): Promise<void> {
    const persisted = tokenStore.set(accessToken, refreshToken);

    // Also store in memory for immediate use
    this.accessToken = accessToken;
    return Promise.resolve(persisted);
  }

  /** Signed out for good: forget the tokens, remember why (when the server said) and go to the sign-in page. The app never reloads: it tells the sign-in screen. */
  private endSession(reason: string | null) {
    this.clearTokens();
    if (isNative()) {
      const detail: SessionExpired = { code: reason ?? 'session_ended', message: reason === 'refresh_reused' ? REFRESH_REUSED_MESSAGE : SESSION_ENDED_MESSAGE };
      window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT, { detail }));
      return;
    }
    if (reason) {
      try {
        window.sessionStorage.setItem(SESSION_ENDED_KEY, reason);
      } catch {
        // storage blocked: the sign-in page simply shows no reason
      }
    }
    window.location.href = '/login';
  }

  private clearTokens() {
    // Clear from both cookies and memory
    void tokenStore.clear();
    this.accessToken = null;
  }

  private refreshAccessToken(): Promise<string> {
    // One refresh at a time. The promise is assigned before its body runs and cleared once it settles, so a refresh that fails
    // at once (no refresh token) can never stay here and fail every later refresh.
    this.refreshPromise ??= this.runRefresh().finally(() => {
      this.refreshPromise = null;
    });
    return this.refreshPromise;
  }

  private async runRefresh(): Promise<string> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      throw new Error(NO_REFRESH_TOKEN);
    }

    const response = isNative()
      ? await this.postNativeRefresh(refreshToken)
      : await axios.post(`${API_BASE_URL}/v1/auth/refresh`, {}, {
          headers: {
            'X-Refresh-Token': refreshToken,
          },
        });

    // never store over a sign-out or another sign-in that happened while the answer was on its way
    if (this.getRefreshToken() !== refreshToken) {
      throw new Error(SESSION_CHANGED);
    }

    // The API answers { success, data: { access_token, refresh_token } }; the flat camelCase shape is kept as a fallback.
    const body = response.data?.data ?? response.data;
    const accessToken: string | undefined = body?.access_token ?? body?.accessToken;
    const newRefreshToken: string | undefined = body?.refresh_token ?? body?.refreshToken;
    if (!accessToken || !newRefreshToken) {
      throw new Error('Refresh response carried no tokens');
    }
    // stored before resolving: an app killed right after the answer must not keep only the used token
    await this.setTokens(accessToken, newRefreshToken);
    return accessToken;
  }

  /**
   * The app's refresh. An answer lost on the network (or a 5xx) is retried once with the same token: within 10 minutes the
   * server answers the same successor again (key decision 6). The token is never rotated locally.
   */
  private async postNativeRefresh(refreshToken: string) {
    const version = this.nativeAppVersion;
    const send = () => axios.post(`${API_BASE_URL}/v1/mobile/sessions/refresh`, {}, {
      headers: { 'X-Refresh-Token': refreshToken, ...(version && { 'X-App-Version': version }) },
      timeout: NATIVE_REFRESH_TIMEOUT_MS,
    });
    try {
      return await send();
    } catch (error) {
      const lost = axios.isAxiosError(error) && (!error.response || error.response.status >= 500);
      if (!lost) throw withoutRefreshToken(error);
      try {
        return await send();
      } catch (retryError) {
        throw withoutRefreshToken(retryError);
      }
    }
  }

  /**
   * The app: the session ends only when the server refused the refresh (401 invalid_refresh_token / refresh_reused, or 404 when
   * the mobile app is switched off for the business). Lost on
   * the network, 5xx or 429: the tokens stay, and the next request refreshes again.
   */
  private endNativeSessionIfRefused(error: unknown) {
    // no refresh token to try (a failed write, or a profile-only start): to sign-in, rather than failing every request
    if (error instanceof Error && error.message === NO_REFRESH_TOKEN) {
      const detail: SessionExpired = { code: 'no_refresh_token', message: SESSION_ENDED_MESSAGE };
      window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT, { detail }));
      return;
    }
    // mobile.md: a 404 on a mobile route means the `mobile_app` flag is off for this business. That is final too: keeping the
    // session would leave every request failing and every outbox item held.
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      if (!this.getRefreshToken()) return; // already ended by a concurrent request
      this.clearTokens();
      const detail: SessionExpired = { code: 'mobile_app_disabled', message: MOBILE_APP_DISABLED_MESSAGE };
      window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT, { detail }));
      return;
    }
    if (!axios.isAxiosError(error) || error.response?.status !== 401) return;
    if (!this.getRefreshToken()) return; // already ended by a concurrent request
    // the server's two final answers: the envelope 401 `invalid_refresh_token`, and the flat 401 `session_ended` whose `reason` says why (`refresh_reused` included)
    const code = (isSessionEnded(error) ? sessionEndedReason(error) : errorCode(error.response.data)) ?? 'invalid_refresh_token';
    this.clearTokens();
    const detail: SessionExpired = { code, message: code === 'refresh_reused' ? REFRESH_REUSED_MESSAGE : code === 'access_ended' ? ACCESS_ENDED_MESSAGE : SESSION_ENDED_MESSAGE };
    window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT, { detail }));
  }

  /** The app's version for the refresh's `X-App-Version`, set by the native bootstrap (this module never loads native code). */
  private nativeAppVersion: string | null = null;

  public setNativeAppVersion(version: string | null) {
    this.nativeAppVersion = version;
  }

  /** The app's cold start: there is no access token in memory, only the stored refresh token. */
  public async refreshSession(): Promise<string> {
    try {
      return await this.refreshAccessToken();
    } catch (error) {
      if (isNative()) this.endNativeSessionIfRefused(error);
      throw error;
    }
  }

  /** Resolves once the store has written them (on the web at once). */
  public setAuthTokens(accessToken: string, refreshToken: string): Promise<void> {
    return this.setTokens(accessToken, refreshToken);
  }

  public clearAuth() {
    this.clearTokens();
  }

  // --- Platform super-admin session & impersonation -------------------------

  // The console and impersonation are web-only (the app never shows them) and keep their tokens in cookies on purpose, so
  // they use the cookie store directly and refuse to run in the native build instead of silently bypassing tokenStore.
  private webOnlyStore() {
    if (isNative()) throw new Error('Console and impersonation sessions are web-only');
    return cookieTokenStore;
  }

  // Establish a platform-admin session. The platform token is stored as the
  // active access_token (so the interceptor attaches it to /platform/* calls).
  // No refresh token — platform tokens are not refreshable.
  public setPlatformSession(token: string) {
    this.webOnlyStore().setAccessOnly(token, 1 / 3);
    Cookies.remove('platform_access_token', { path: '/' });
    this.accessToken = token;
  }

  // Swap the active token to an impersonation token, stashing the platform token.
  // Removes the refresh token so the 401 interceptor can never mint a
  // wrong-identity token while impersonating.
  public enterImpersonation(impersonationToken: string) {
    const store = this.webOnlyStore(); // before the stash write, so a refusal leaves no cookie behind
    const platform = tokenStore.getAccess();
    if (platform) {
      Cookies.set('platform_access_token', platform, secureCookie());
    }
    store.setAccessOnly(impersonationToken, 1 / 48); // ~30 min, and no refresh token
    this.accessToken = impersonationToken;
  }

  private endingImpersonation: Promise<void> | null = null;

  /** True from the moment the admin presses Stop until the platform session is restored: customer calls refused meanwhile are expected. */
  private stoppingImpersonation = false;

  public beginStoppingImpersonation() {
    this.stoppingImpersonation = true;
  }

  public doneStoppingImpersonation() {
    this.stoppingImpersonation = false;
  }

  private async endImpersonationAfterExpiry(): Promise<void> {
    const sessionId = readStash()?.sessionId;
    const platformToken = this.getPlatformToken();
    const back = returnPath('/platform');
    if (sessionId && platformToken) {
      // best effort: close the audit record while the platform token is still at hand
      await this.client
        .post(`/v1/platform/impersonations/${sessionId}/stop`, {}, { headers: { Authorization: `Bearer ${platformToken}` } })
        .catch(() => undefined);
    }
    this.exitImpersonation();
    clearStash();
    window.location.href = back;
  }

  // Restore the stashed platform token. Idempotent: with no stash, clear only when the active token is not a platform token.
  public exitImpersonation() {
    const platform = Cookies.get('platform_access_token');
    Cookies.remove('platform_access_token', { path: '/' });
    if (platform) {
      this.webOnlyStore().setAccessOnly(platform, 1 / 3);
      this.accessToken = platform;
    } else if (!isPlatformToken(tokenStore.getAccess())) {
      this.clearTokens();
    }
  }

  public get isImpersonating(): boolean {
    return !!Cookies.get('platform_access_token');
  }

  public getPlatformToken(): string | null {
    return Cookies.get('platform_access_token') || null;
  }

  public get instance() {
    return this.client;
  }
}

export const apiClient = new ApiClient();
export default apiClient.instance;
